// Helper function to format email time - always show CST time in 12-hour format
export const formatEmailTime = (dateString) => {
  if (!dateString) return '';
  const date = new Date(dateString);
  
  // Check if the date is valid
  if (isNaN(date.getTime())) return '';
  
  const now = new Date();
  const today = new Date(now.toLocaleString('en-US', { timeZone: 'America/Chicago' }));
  const emailDate = new Date(date.toLocaleString('en-US', { timeZone: 'America/Chicago' }));
  
  // Format time in 12-hour CST format
  const timeStr = date.toLocaleTimeString('en-US', { 
    hour: 'numeric', 
    minute: '2-digit',
    hour12: true,
    timeZone: 'America/Chicago'
  }).toLowerCase();
  
  // Check if same day
  const isToday = today.toDateString() === emailDate.toDateString();
  
  if (isToday) {
    return timeStr;
  }
  
  // Check if yesterday
  const yesterday = new Date(today);
  yesterday.setDate(yesterday.getDate() - 1);
  if (yesterday.toDateString() === emailDate.toDateString()) {
    return `Yesterday ${timeStr}`;
  }
  
  // Otherwise show date and time
  const dateStr = date.toLocaleDateString('en-US', { 
    month: 'short', 
    day: 'numeric',
    timeZone: 'America/Chicago'
  });
  return `${dateStr} ${timeStr}`;
};

// Helper function to categorize emails
export const categorizeEmail = (email) => {
  const subject = (email.subject || '').toLowerCase();
  const from = (email.from || email.from_email || email.sender || '').toLowerCase();
  const body = (email.body || email.body_preview || email.snippet || email.preview || '').toLowerCase();
  const to = (email.to || '').toLowerCase();
  
  // Combine all text for comprehensive scanning
  const allText = `${subject} ${from} ${body} ${to}`;
  
  // Check for dollar amounts (e.g., $10, $99.99, $1,000.00)
  const hasDollarAmount = /\$\d+(?:,\d{3})*(?:\.\d{2})?/.test(subject + body);
  
  // URGENT - Time-sensitive matters
  const urgentKeywords = [
    'urgent', 'asap', 'immediately', 'critical', 'emergency', 'time-sensitive',
    'action required', 'immediate attention', 'priority', 'deadline', 'overdue',
    'need help', 'please respond', 'waiting for', 'as soon as possible', 'deadline today'
  ];
  if (urgentKeywords.some(keyword => allText.includes(keyword))) {
    return 'urgent';
  }
  
  // TRAVEL - Flights, bookings, itineraries, boarding passes
  const travelKeywords = [
    'flight', 'booking confirmation', 'itinerary', 'boarding pass', 'check-in',
    'airline', 'airport', 'departure', 'arrival', 'layover', 'gate',
    'seat assignment', 'e-ticket', 'travel confirmation', 'trip confirmation',
    'hotel reservation', 'hotel booking', 'car rental', 'rental confirmation',
    'united airlines', 'american airlines', 'delta', 'southwest', 'jetblue',
    'british airways', 'lufthansa', 'emirates', 'frontier', 'spirit airlines',
    'alaska airlines', 'hawaiian airlines', 'air france', 'klm',
    'booking.com', 'expedia', 'kayak', 'priceline', 'tripadvisor',
    'marriott', 'hilton', 'hyatt', 'airbnb', 'vrbo',
    'tsa precheck', 'global entry', 'passport', 'visa application',
    'fly', 'nonstop', 'round trip', 'one way', 'class of service',
    'baggage', 'carry-on', 'checked bag', 'travel insurance'
  ];
  const travelSenders = [
    'airline', 'airlines', 'booking', 'expedia', 'kayak', 'travel',
    'marriott', 'hilton', 'hyatt', 'airbnb', 'hertz', 'avis', 'enterprise'
  ];
  if (travelKeywords.some(keyword => allText.includes(keyword)) ||
      travelSenders.some(sender => from.includes(sender))) {
    return 'travel';
  }
  
  // ORDERS - Receipt/Invoice/Order related OR contains dollar amounts
  const orderKeywords = [
    'receipt', 'invoice', 'order', 'purchase', 'transaction', 'payment received',
    'order confirmation', 'order shipped', 'order delivered', 'tracking number',
    'your order', 'order #', 'invoice #', 'receipt #', 'payment confirmation',
    'billing statement', 'subscription renewed', 'charge', 'refund', 'paid',
    'total:', 'amount:', 'price', 'cost', 'fee', 'payment', 'shipped',
    'delivery', 'out for delivery', 'has shipped', 'amazon', 'ebay', 'paypal',
    'venmo', 'zelle', 'apple receipt', 'google play'
  ];
  if (hasDollarAmount || orderKeywords.some(keyword => allText.includes(keyword))) {
    return 'orders';
  }
  
  // MEETING - Tours, meetings, calls, scheduling
  const meetingKeywords = [
    'meeting', 'schedule', 'calendar', 'appointment', 'call', 'zoom', 'teams',
    'tour', 'showing', 'viewing', 'walkthrough', 'site visit', 'property tour',
    'phone call', 'video call', 'conference', 'webinar', 'demo', 'presentation',
    'reschedule', 'agenda', 'invite', 'invitation', 'rsvp', 'confirm attendance',
    'availability', 'book a time', 'schedule a call', 'let\'s meet', 'meet up',
    'catch up', 'touch base', 'check in', 'sync up', 'huddle', 'standup',
    'google meet', 'microsoft teams', 'webex', 'gotomeeting'
  ];
  if (meetingKeywords.some(keyword => allText.includes(keyword))) {
    return 'meeting';
  }
  
  // CLIENT - Business/client communications
  const clientKeywords = [
    'contract', 'agreement', 'proposal', 'quote', 'estimate', 'project',
    'customer', 'client', 'deal', 'opportunity', 'partnership', 'collaboration',
    'account update', 'service update', 'renewal notice', 'subscription',
    'inquiry', 'interested in', 'property', 'listing', 'looking to buy', 
    'looking to sell', 'offer', 'closing', 'escrow', 'commission'
  ];
  if (clientKeywords.some(keyword => allText.includes(keyword))) {
    return 'client';
  }
  
  // REPORT - Reports, newsletters, automated notifications
  const reportKeywords = [
    'report', 'analytics', 'summary', 'digest', 'newsletter', 'update',
    'notification', 'automated', 'weekly', 'monthly', 'daily report',
    'performance', 'metrics', 'statistics', 'insights', 'dashboard',
    'status update', 'changelog', 'release notes', 'security alert',
    'unsubscribe', 'marketing', 'promotion'
  ];
  if (reportKeywords.some(keyword => allText.includes(keyword))) {
    return 'report';
  }
  
  // Default to 'other' for uncategorized emails
  return 'other';
};

// Format phone number for display
export const formatPhoneNumber = (phone) => {
  if (!phone) return '';
  const cleaned = phone.replace(/\D/g, '');
  if (cleaned.length === 10) {
    return `(${cleaned.slice(0,3)}) ${cleaned.slice(3,6)}-${cleaned.slice(6)}`;
  }
  if (cleaned.length === 11 && cleaned.startsWith('1')) {
    return `+1 (${cleaned.slice(1,4)}) ${cleaned.slice(4,7)}-${cleaned.slice(7)}`;
  }
  return phone;
};

// Format timestamp for messages - always show CST time in 12-hour format
export const formatMessageTime = (timestamp) => {
  if (!timestamp) return '';
  const date = new Date(timestamp);
  
  // Format as 12-hour time in CST (America/Chicago)
  return date.toLocaleTimeString('en-US', { 
    hour: 'numeric', 
    minute: '2-digit',
    hour12: true,
    timeZone: 'America/Chicago'
  }).toLowerCase();
};

// Get initials from name
export const getInitials = (name) => {
  if (!name) return '?';
  const parts = name.trim().split(' ').filter(Boolean);
  if (parts.length === 0) return '?';
  if (parts.length === 1) return parts[0][0]?.toUpperCase() || '?';
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
};

// Generate avatar color based on name
export const getAvatarColor = (name) => {
  if (!name) return 'bg-gray-600';
  const colors = [
    'bg-blue-600', 'bg-green-600', 'bg-purple-600', 'bg-pink-600',
    'bg-indigo-600', 'bg-teal-600', 'bg-orange-600', 'bg-cyan-600'
  ];
  let hash = 0;
  for (let i = 0; i < name.length; i++) {
    hash = name.charCodeAt(i) + ((hash << 5) - hash);
  }
  return colors[Math.abs(hash) % colors.length];
};

// Category badge colors
export const getCategoryBadgeColor = (category) => {
  const colors = {
    personal: 'bg-pink-500/20 text-pink-400 border-pink-500/30',
    business: 'bg-green-500/20 text-green-400 border-green-500/30',
    client: 'bg-blue-500/20 text-blue-400 border-blue-500/30',
    urgent: 'bg-red-500/20 text-red-400 border-red-500/30',
    'follow-up': 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30',
    report: 'bg-purple-500/20 text-purple-400 border-purple-500/30',
    general: 'bg-gray-500/20 text-gray-400 border-gray-500/30',
  };
  return colors[category?.toLowerCase()] || colors.general;
};

// Call direction badge
export const getCallDirectionBadge = (direction) => {
  if (direction === 'inbound') {
    return { label: '↓ Inbound', className: 'bg-green-500/20 text-green-400 border-green-500/30' };
  }
  return { label: '↑ Outbound', className: 'bg-blue-500/20 text-blue-400 border-blue-500/30' };
};

// Call outcome badge
export const getCallOutcomeBadge = (outcome) => {
  const outcomes = {
    'booked': { label: '📅 Booked', className: 'bg-green-500 text-white' },
    'hot_lead': { label: '🔥 Hot Lead', className: 'bg-orange-500 text-white' },
    'follow_up': { label: '🔄 Follow-up', className: 'bg-blue-500 text-white' },
    'qualified': { label: '✓ Qualified', className: 'bg-purple-500 text-white' },
    'not_interested': { label: '✗ Not Interested', className: 'bg-gray-600 text-gray-200' },
    'voicemail': { label: '📧 Voicemail', className: 'bg-gray-600 text-gray-200' }
  };
  return outcomes[outcome] || outcomes.voicemail;
};

// Sentiment badge
export const getSentimentBadge = (sentiment) => {
  const sentiments = {
    'positive': { emoji: '😊', className: 'bg-green-500/20 text-green-400 border border-green-500/30' },
    'negative': { emoji: '😟', className: 'bg-red-500/20 text-red-400 border border-red-500/30' },
    'neutral': { emoji: '😐', className: 'bg-gray-500/20 text-gray-400 border border-gray-500/30' }
  };
  return sentiments[sentiment] || sentiments.neutral;
};

// Lead quality badge
export const getLeadQualityBadge = (quality) => {
  const qualities = {
    'hot': { label: '🔥 Hot', className: 'bg-orange-500/20 text-orange-400 border border-orange-500/30' },
    'warm': { label: '☀️ Warm', className: 'bg-yellow-500/20 text-yellow-400 border border-yellow-500/30' },
    'cold': { label: '❄️ Cold', className: 'bg-blue-500/20 text-blue-400 border border-blue-500/30' }
  };
  return qualities[quality] || qualities.cold;
};

// Format call duration
export const formatCallDuration = (seconds) => {
  if (!seconds) return '';
  const mins = Math.floor(seconds / 60);
  const secs = seconds % 60;
  if (mins > 0) {
    return `${mins}m ${secs}s`;
  }
  return `${secs}s`;
};

// Phone type badge colors
export const getPhoneTypeBadgeColor = (phoneType) => {
  if (phoneType === 'business') {
    return 'bg-green-500/20 text-green-400 border border-green-500/30';
  }
  return 'bg-pink-500/20 text-pink-400 border border-pink-500/30';
};
